import React, { memo } from 'react'
import PropTypes from 'prop-types'
import styled from 'react-emotion'
import { injectGlobal } from 'emotion'
import { ReactComponent as SortIcon } from '../../media/img/icons/sort.svg'
import eot from '../../media/fonts/wiip-icons.eot'
import woff from '../../media/fonts/wiip-icons.woff'
import ttf from '../../media/fonts/wiip-icons.ttf'
import svg from '../../media/fonts/wiip-icons.svg'

const size = 10

// Font Icons

injectGlobal({
  '@font-face': {
    fontFamily: 'wiip-icons',
    src: [
      `url("${eot}")`,
      `url("${eot}?#iefix") format("embedded-opentype"), url("${woff}") format("woff"), url("${ttf}") format("truetype"), url("${svg}#projo-icons") format("svg")`
    ],
    fontWeight: 'normal',
    fontStyle: 'normal'
  }
})

const icons = {
  more: '\\e000',
  cog: '\\e00a',
  info: '\\e009',
  'arrow-up-right': '\\e00f',
  'arrow-right': '\\e00d',
  'arrow-left': '\\e00c',
  link: '\\e010',
  x: '\\e00b',
  plus: '\\e004',
  'circle-filled': '\\e003',
  'arrow-up-down': '\\e00e',
  search: '\\e011',
  circle: '\\e008',
  menu: '\\e012',
  'arrowhead-right-thick': '\\e005',
  'arrowhead-down-thick': '\\e006',
  'arrowhead-down': '\\e001',
  'arrowhead-up': '\\e001',
  crop: '\\e002',
  'arrowhead-right': '\\e013',
  replace: '\\e007',
  none: '\\e00b' // will be made transparent
}

export const FontIcon = styled('span')(({ icon }) => ({
  label: 'Icon',

  '.row > &:first-child': { marginLeft: 11 },
  '.row > &:last-child': { marginRight: 11 },
  '&:before': {
    content: `"${icons[icon] || ''}"`,
    display: 'inline-block',
    fontFamily: '"wiip-icons" !important',
    fontSize: size,
    fontStyle: 'normal !important',
    fontWeight: 'normal !important',
    fontVariant: 'normal !important',
    textTransform: 'none !important',
    speak: 'none',
    lineHeight: 1,
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
    color: icon === 'none' && 'transparent',

    transform: icon === 'arrowhead-up' && 'rotate(180deg)'
  }
}))

// Loaders

export const Loader = styled('span')(({ icon }) => ({
  label: 'IconLoader',
  display: 'inline-block',
  width: size,
  height: size,
  borderRadius: icon === 'loader' ? size / 2 : 2,
  border: '2px solid currentColor',
  position: 'relative',

  '.row > &:first-child': { marginLeft: 11 },
  '.row > &:last-child': { marginRight: 11 },

  '&:before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    borderWidth: 3,
    borderStyle: 'solid',
    borderColor: 'inherit',
    borderRadius: icon === 'loader' ? 3 : 0,
    top: 0,
    left: 0,
    WebkitAnimation: 'pulse .8s infinite alternate',
    animation: 'pulse .8s infinite alternate'
    // pulse is defined in index.html because it's used before react app loads
  }
}))

// SVG icons

const SvgIcon = styled(({ icon, className }) => {
  switch (icon) {
    case 'sort':
      return <SortIcon className={className} />
    default:
      return null
  }
})({
  fill: 'currentColor'
})

// Special Icons
const EllipsisIcon = styled(({ className }) => (
  <span className={className}>•••</span>
))({ fontSize: 11.5, letterSpacing: 0.8, paddingBottom: 1 })

export const Icon = memo(({ icon, ...rest }) => {
  switch (icon) {
    case 'board':
      return <BoardIcon {...rest} />
    case 'folder':
      return <FolderIcon {...rest} />
    case 'loader':
    case 'loader-square':
      return <Loader icon={icon} {...rest} />
    case 'ellipsis':
      return <EllipsisIcon {...rest} />
    // SVGs
    case 'sort':
      return <SvgIcon icon={icon} {...rest} />
    // Font Icons
    default:
      return <FontIcon icon={icon} {...rest} />
  }
})
Icon.propTypes = {
  icon: PropTypes.string.isRequired
}

export default Icon

export const BoardIcon = styled('span')(({ color, active = false }) => ({
  flex: 'none',
  display: 'inline-block',
  width: size,
  height: size,
  borderRadius: size / 2,
  border: `2px solid ${color || 'currentColor'}`,
  backgroundColor: active && (color || 'currentColor'),
  // when directly inside a Row, not within a label or button more space is needed
  '.row > &:first-child': { marginLeft: 11 },
  '.row > &:last-child': { marginRight: 11 }
}))
BoardIcon.propTypes = {
  color: PropTypes.string
}

export const FolderIcon = styled('span')(({ color }) => ({
  flex: 'none',
  display: 'inline-block',
  width: size,
  height: size,
  borderRadius: 1,
  backgroundColor: color || 'currentColor',
  // when directly inside a Row, not within a label or button more space is needed
  '.row > &:first-child': { marginLeft: 11 },
  '.row > &:last-child': { marginRight: 11 }
}))
FolderIcon.propTypes = {
  color: PropTypes.string
}
